import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LEGAL_FORM_LABELS, getLegalFormTone, MANAGEMENT_MODE_LABELS } from "@/constants/group";
import { formatPercent } from "@/lib/format";
import { Building2, Users2 } from "lucide-react";

/**
 * Iterative organigram (BFS by depth).
 * Groups nodes by depth from the roots and draws each level horizontally.
 * A dedicated "detached" level shows companies with no direct owner but
 * that aren't roots owning anyone either (edge case: isolated leaves).
 */
export default function OrgChartTab() {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/group/orgchart").then(({ data }) => {
      setNodes(data.nodes || []); setEdges(data.edges || []); setRoots(data.roots || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const byId = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);

  const childrenByParent = useMemo(() => {
    const m = {};
    edges.forEach((e) => { (m[e.from_id] = m[e.from_id] || []).push(e); });
    return m;
  }, [edges]);

  // BFS: compute depth per node from roots
  const levels = useMemo(() => {
    const depth = {};
    const queue = roots.map((id) => [id, 0]);
    while (queue.length) {
      const [id, d] = queue.shift();
      if (depth[id] !== undefined) continue;
      depth[id] = d;
      (childrenByParent[id] || []).forEach((e) => queue.push([e.to_id, d + 1]));
    }
    // Nodes not reached (isolated) go on level 0
    nodes.forEach((n) => { if (depth[n.id] === undefined) depth[n.id] = 0; });
    const grouped = {};
    Object.entries(depth).forEach(([id, d]) => {
      (grouped[d] = grouped[d] || []).push(id);
    });
    // Sort by parent order within each depth for stability
    return Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      .map((d) => ({ depth: Number(d), ids: grouped[d] }));
  }, [nodes, roots, childrenByParent]);

  if (loading) return <div className="text-muted-foreground text-sm">Chargement…</div>;
  if (nodes.length === 0) return (
    <div className="card-elevated p-12 text-center text-muted-foreground">
      <Users2 className="mx-auto h-8 w-8 mb-3" />
      Aucune entité à afficher.
    </div>
  );

  return (
    <div className="card-elevated p-6 lg:p-10 overflow-x-auto" data-testid="orgchart">
      <div className="flex flex-col gap-10 min-w-max">
        {levels.map((lvl, idx) => (
          <div key={lvl.depth} className="relative">
            <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
              {lvl.depth === 0 ? "Niveau racine" : `Niveau ${lvl.depth}`}
            </div>
            <div className="flex flex-wrap gap-6 lg:gap-8">
              {lvl.ids.map((id) => {
                const node = byId[id];
                const parentEdges = edges.filter((e) => e.to_id === id);
                return (
                  <div key={id} className="flex flex-col items-center">
                    {parentEdges.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1 justify-center max-w-[240px]">
                        {parentEdges.map((e) => (
                          <span key={e.id}
                                className="text-[10px] font-mono rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">
                            {formatPercent(e.ownership_pct)} ← {(byId[e.from_id]?.denomination || "?").slice(0, 18)}
                          </span>
                        ))}
                      </div>
                    )}
                    <NodeCard node={node} />
                  </div>
                );
              })}
            </div>
            {idx < levels.length - 1 && (
              <div className="mt-6 h-px w-full bg-border/60" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function NodeCard({ node }) {
  if (!node) return null;
  const tone = getLegalFormTone(node.legal_form);
  const isReference = node.management_mode === "reference";
  return (
    <Link
      to={`/app/companies/${node.id}`}
      data-testid={`orgchart-node-${node.id}`}
      className={`block min-w-[220px] max-w-[260px] rounded-2xl border-2 bg-card p-4 hover:shadow-elevated transition-all ${tone.border} ${isReference ? "border-dashed" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone.bg} ${tone.text}`}>
          <Building2 className="h-4 w-4" />
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-widest ${tone.text}`}>
          {LEGAL_FORM_LABELS[node.legal_form] || node.legal_form}
        </span>
      </div>
      <div className="mt-3 font-heading text-sm leading-tight">{node.denomination}</div>
      {node.activity && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{node.activity}</div>}
      {isReference && (
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-accent">
          {MANAGEMENT_MODE_LABELS.reference}
        </div>
      )}
    </Link>
  );
}
